import { FilterOptions } from "@/context/SearchContext";

/**
 * Clean filters to remove empty arrays and undefined values
 * This ensures only explicitly selected filters are included
 */
export function cleanFilters(filters: FilterOptions): FilterOptions {
  const cleaned: FilterOptions = {};

  Object.entries(filters).forEach(([key, values]) => {
    // Only include filters that have actual selections (non-empty arrays)
    if (Array.isArray(values) && values.length > 0) {
      cleaned[key as keyof FilterOptions] = values;
    }
  });

  return cleaned;
}

/**
 * Extract only the collections selection from a set of filters.
 * Collections is a special filter (scopes which libraries to search) and must
 * survive a new search / "clear all filters" while other filters reset.
 */
export function pickCollections(filters: FilterOptions): FilterOptions {
  return filters.collections?.length ? { collections: filters.collections } : {};
}

/**
 * SearchState maintains the complete search state as a structured object
 * and provides methods to serialize/deserialize from URL
 */
export interface SearchState {
  term?: string;
  filters: FilterOptions;
  locale?: string;
  [key: string]: string | string[] | FilterOptions | undefined; // Allow for additional params with specific types
}

export class SearchStateManager {
  private state: SearchState;

  constructor(initialState?: SearchState | URLSearchParams | string) {
    if (typeof initialState === "string") {
      // Initialize from URL string
      this.state = SearchStateManager.fromURL(initialState);
    } else if (initialState instanceof URLSearchParams) {
      // Initialize from URLSearchParams
      this.state = SearchStateManager.fromParams(initialState);
    } else {
      // Initialize from state object or default
      this.state = initialState || { filters: {} };
    }
  }

  /**
   * Get current state
   */
  getState(): SearchState {
    return { ...this.state };
  }

  /**
   * Update search term
   */
  setTerm(term: string | undefined): this {
    if (term?.trim()) {
      this.state.term = term;
    } else {
      delete this.state.term;
    }
    return this;
  }

  /**
   * Update a specific filter
   */
  setFilter(key: keyof FilterOptions, values: string[]): this {
    if (!this.state.filters) {
      this.state.filters = {};
    }

    if (values.length > 0) {
      this.state.filters[key] = values;
    } else {
      delete this.state.filters[key];
    }
    return this;
  }

  /**
   * Update all filters
   */
  setFilters(filters: FilterOptions): this {
    this.state.filters = filters;
    return this;
  }

  /**
   * Toggle a filter value
   */
  toggleFilterValue(key: keyof FilterOptions, value: string): this {
    if (!this.state.filters) {
      this.state.filters = {};
    }

    const currentValues = this.state.filters[key] || [];
    const index = currentValues.indexOf(value);

    if (index >= 0) {
      // Remove value
      const newValues = currentValues.filter((v) => v !== value);
      this.setFilter(key, newValues);
    } else {
      // Add value
      this.setFilter(key, [...currentValues, value]);
    }

    return this;
  }

  /**
   * Set additional parameter
   */
  setParam(key: string, value: string | string[] | undefined): this {
    if (value !== undefined && value !== null && value !== "") {
      this.state[key] = value;
    } else {
      delete this.state[key];
    }
    return this;
  }

  /**
   * Clear all filters but preserve other params
   */
  clearFilters(): this {
    this.state.filters = {};
    return this;
  }

  /**
   * Clear everything
   */
  clear(): this {
    this.state = { filters: {} };
    return this;
  }

  /**
   * Convert state to URLSearchParams
   */
  toURLSearchParams(): URLSearchParams {
    const params = new URLSearchParams();

    // Add term
    if (this.state.term) {
      params.set("term", this.state.term);
    }

    // Add filters using array notation
    if (this.state.filters) {
      Object.entries(this.state.filters).forEach(([key, values]) => {
        if (values && values.length > 0) {
          values.forEach((value: string) => {
            params.append(`${key}[]`, value);
          });
        }
      });
    }

    // Add other params
    Object.entries(this.state).forEach(([key, value]) => {
      if (key !== "term" && key !== "filters" && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, String(v)));
        } else {
          params.set(key, String(value));
        }
      }
    });

    return params;
  }

  /**
   * Convert state to URL string
   */
  toURL(basePath?: string): string {
    const params = this.toURLSearchParams();
    const queryString = params.toString();

    if (basePath) {
      return queryString ? `${basePath}?${queryString}` : basePath;
    }

    return queryString ? `?${queryString}` : "";
  }

  /**
   * Parse URLSearchParams into SearchState
   */
  static fromParams(params: URLSearchParams): SearchState {
    const state: SearchState = { filters: {} };

    // Extract term
    const term = params.get("term");
    if (term) {
      state.term = term;
    }

    // Extract filters
    const filterKeys: (keyof FilterOptions)[] = [
      "imprint",
      "payment",
      "licenses",
      "publishing_model",
      "collections",
      "quality_score",
    ];

    filterKeys.forEach((key) => {
      // Try array notation first
      let values = params.getAll(`${key}[]`);
      // Fallback to non-array notation
      if (values.length === 0) {
        values = params.getAll(key);
      }
      if (values.length > 0) {
        state.filters[key] = values;
      }
    });

    // Extract other params
    const knownParams = new Set([
      "term",
      ...filterKeys,
      ...filterKeys.map((k) => `${k}[]`),
    ]);

    params.forEach((value, key) => {
      if (!knownParams.has(key)) {
        // Handle multiple values
        const values = params.getAll(key);
        state[key] = values.length > 1 ? values : value;
      }
    });

    return state;
  }

  /**
   * Parse URL string into SearchState
   */
  static fromURL(url: string): SearchState {
    try {
      const urlObj = new URL(url, "http://example.com"); // Use dummy base for relative URLs
      return SearchStateManager.fromParams(new URLSearchParams(urlObj.search));
    } catch {
      return { filters: {} };
    }
  }

  /**
   * Create a copy of the manager
   */
  clone(): SearchStateManager {
    return new SearchStateManager(JSON.parse(JSON.stringify(this.state)));
  }
}

